import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { AuthStore } from '@app/stores/auth.store';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';

type UiStatus = 'ok' | 'expired' | 'invalid';

@Component({
  selector: 'app-meeting-offer-response',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './meeting-offer-response.component.html',
  styleUrl: './meeting-offer-response.component.scss',
})
export class MeetingOfferResponseComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly projectAdminService = inject(ProjectAdminService);

  readonly loading = signal(true);
  readonly status = signal<UiStatus | null>(null);

  ngOnInit(): void {
    const meetingId = this.route.snapshot.queryParamMap.get('meetingId') ?? '';
    const numberParam = this.route.snapshot.queryParamMap.get('number') ?? '';
    const parsed = this.parseNumberAndSubaccount(numberParam);
    const dateId = parsed.dateId;
    const subaccount =
      this.route.snapshot.queryParamMap.get('subaccount') ?? parsed.subaccount;

    if (!meetingId || !this.isAllowedDateId(dateId)) {
      this.loading.set(false);
      this.status.set('invalid');
      return;
    }

    this.ensureCompanyContext(subaccount)
      .pipe(
        switchMap((canContinue) =>
          canContinue
            ? this.projectAdminService.acceptMeetingByCustomer(meetingId, dateId)
            : of(null),
        ),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: (result) => {
          if (result === null) {
            return;
          }

          this.status.set('ok');
          this.router.navigate([], {
            relativeTo: this.route,
            replaceUrl: true,
            queryParams: {
              meetingId: null,
              number: null,
              subaccount: null,
            },
            queryParamsHandling: 'merge',
          });
        },
        error: (err) => this.status.set(this.resolveErrorStatus(err)),
      });
  }

  private isAllowedDateId(dateId: string): boolean {
    return dateId === '1' || dateId === '2' || dateId === '3';
  }

  private parseNumberAndSubaccount(value: string): {
    dateId: string;
    subaccount: string | null;
  } {
    if (!value) {
      return { dateId: '', subaccount: null };
    }

    const decodedValue = this.safeDecode(value);
    const marker = '?subaccount=';
    const markerIndex = decodedValue.toLowerCase().indexOf(marker);
    if (markerIndex === -1) {
      return { dateId: decodedValue, subaccount: null };
    }

    const dateId = decodedValue.slice(0, markerIndex);
    const subaccount = decodedValue.slice(markerIndex + marker.length);
    return { dateId, subaccount: subaccount || null };
  }

  private ensureCompanyContext(subaccount: string | null): Observable<boolean> {
    if (!subaccount) {
      return of(true);
    }

    const loggedInCompanyId = this.authStore.loggedInCompanyId();
    if (
      loggedInCompanyId &&
      loggedInCompanyId.toLowerCase() === subaccount.toLowerCase()
    ) {
      return of(true);
    }

    const rememberMe = JSON.parse(localStorage.getItem('rememberMe') || 'false');
    return this.authStore.loginAsCompany(subaccount, rememberMe).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  private resolveErrorStatus(err: any): UiStatus {
    const joined = `${err?.error?.type ?? ''} ${err?.error?.title ?? ''} ${err?.error?.message ?? ''}`.toLowerCase();
    return joined.includes('expired') ? 'expired' : 'invalid';
  }

  private safeDecode(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
}
